import React, { useEffect, useRef, useState } from 'react';
import DsSelectBase from '../../common/DsSelect';
import DsButton from '../../common/DsButton';
import { api } from '../../../api/client';
import '../../../css/admin/chats/ChatCastingSearchFields.css';
import type { paths } from '../../../api/types.gen';

const DsSelect = DsSelectBase as React.ComponentType<React.SelectHTMLAttributes<HTMLSelectElement>>;
type Options = paths['/admin/personal-chat/casting-events']['get']['responses'][200]['content']['application/json'];
export type ChatCastingFilter = { eventId: string; eventName: string; kind: 'offer' | 'contract'; status: string; statusLabel: string };
export const emptyChatCastingFilter: ChatCastingFilter = { eventId: '', eventName: '', kind: 'offer', status: '', statusLabel: '' };
export function castingFilterLabel(filter: ChatCastingFilter) {
  return filter.eventId ? `${filter.eventName} / ${filter.kind === 'offer' ? 'オファー' : '本契約'}${filter.statusLabel ? ` / ${filter.statusLabel}` : ''}` : '';
}
export default function ChatCastingSearchFields({ value, onChange }: { value: ChatCastingFilter; onChange: (_value: ChatCastingFilter) => void }) {
  const [keyword, setKeyword] = useState('');
  const [options, setOptions] = useState<Options | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sequence = useRef(0);
  const submittedKeyword = useRef('');
  const load = async (offset = 0) => {
    const seq = ++sequence.current;
    if (!offset) submittedKeyword.current = keyword.trim();
    setLoading(true); setError('');
    try {
      const { data, response } = await api.GET('/admin/personal-chat/casting-events', { params: { query: { kind: value.kind, q: submittedKeyword.current, offset, limit: 20 } } });
      if (seq !== sequence.current) return;
      if (!response.ok || !data || !Array.isArray(data.events) || !Array.isArray(data.statuses)) throw new Error();
      setOptions(current => offset ? { ...data, events: [...(current?.events || []), ...data.events] } : data);
    } catch { if (seq === sequence.current) setError('イベント候補を取得できませんでした。再試行してください。'); }
    finally { if (seq === sequence.current) setLoading(false); }
  };
  useEffect(() => { setOptions(null); void load(); return () => { sequence.current++; }; }, [value.kind]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="chat-casting-search-fields">
    <div className="chat-target-search-form__field">
      <label className="chat-target-search-form__label" htmlFor="chat-casting-kind">送付種別</label>
      <DsSelect className="chat-target-search-form__select" id="chat-casting-kind" value={value.kind} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...value, kind: event.target.value as ChatCastingFilter['kind'], status: '', statusLabel: '' })}>
        <option value="offer">オファー</option><option value="contract">本契約</option>
      </DsSelect>
    </div>
    <div className="chat-target-search-form__field">
      <label className="chat-target-search-form__label" htmlFor="chat-casting-keyword">イベント名で候補を検索</label>
      <div className="chat-casting-search-fields__lookup">
        <input maxLength={120} id="chat-casting-keyword" className="chat-target-search-form__input" value={keyword} onChange={event => { setKeyword(event.target.value); }} placeholder="過去のイベントも名前で検索" onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); event.stopPropagation(); if (!loading) void load(); } }} />
        <DsButton variant="secondary" loading={loading} aria-label="イベント候補を検索" onClick={() => void load()}>候補検索</DsButton>
      </div>
      {error ? <p className="chat-casting-search-fields__error" role="alert">{error}</p> : null}
      {!loading && !error && options?.events.length === 0 ? <p className="chat-casting-search-fields__hint" role="status">権限内に一致するイベントがありません。</p> : null}
    </div>
    <div className="chat-target-search-form__field">
      <label className="chat-target-search-form__label" htmlFor="chat-casting-event">イベント</label>
      <DsSelect className="chat-target-search-form__select" id="chat-casting-event" value={value.eventId} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = options?.events.find(item => item.event_uuid === event.target.value);
        onChange({ ...value, eventId: event.target.value, eventName: selected?.name || '', status: '', statusLabel: '' });
      }}>
        <option value="">指定なし</option>
        {value.eventId && !options?.events.some(item => item.event_uuid === value.eventId) ? <option value={value.eventId}>{value.eventName}</option> : null}
        {options?.events.map(item => <option key={item.event_uuid} value={item.event_uuid}>{item.name}</option>)}
      </DsSelect>
      {options?.hasMore ? <DsButton variant="secondary" loading={loading} onClick={() => void load(options.nextOffset)}>候補をさらに表示</DsButton> : null}
    </div>
    <div className="chat-target-search-form__field">
      <label className="chat-target-search-form__label" htmlFor="chat-casting-status">ステータス</label>
      <DsSelect className="chat-target-search-form__select" id="chat-casting-status" aria-describedby="chat-casting-status-hint" value={value.status} disabled={!value.eventId || loading || !options} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...value, status: event.target.value, statusLabel: options?.statuses.find(item => item.value === event.target.value)?.label || '' })}>
        <option value="">すべて</option>
        {options?.statuses.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
      </DsSelect>
      <p id="chat-casting-status-hint" className="chat-casting-search-fields__hint">{value.eventId ? '選択イベントの最新の送付状態で絞り込みます。' : 'イベントを選ぶとステータスを指定できます。'}</p>
    </div>
  </div>;
}
